import { defineComponent as _defineComponent } from 'vue'
import { ref, onMounted, onUnmounted } from 'vue';
import MenuBar from '@/components/MenuBar.vue';
import Splash from '@/components/Splash.vue';
import OnScreenDebugger from '@/components/OnScreenDebugger.vue';
import { sessionSpend } from '@/utils/mixpanel';
import { preloadCriticalModules } from '@/utils/lazy-load';
import { prewarmCdnOrigins } from '@/utils/cdn-prewarm';
import { memoryMonitor, frameRateMonitor } from '@/config/performance';
import { registerTizenMediaKeys, detectPlatform } from '@/utils/device';
import { initRemoteControl } from './utils/remote-control';
import focusManager from './utils/focus-manager';
import { removeStorage } from './utils/storage';
import { logger } from './utils/dev-logger';
import useUserStore from '@/store/user';
// import { Toast } from './utils/toast';


export default /*@__PURE__*/_defineComponent({
  __name: 'App',
  setup(__props, { expose: __expose }) {
  __expose();

const showSplash = ref(true);

const hiddenSplash = () => {
  showSplash.value = false;
};

onMounted(async () => {
  sessionSpend();
  logger.log('######################## MOUNTING');

  // Sync persisted user session to Conviva on every app startup.
  // setConvivaUser() is also called on login/logout, but those only fire during the
  // active session — returning users with a stored session would get 'NA' as viewer ID.
  useUserStore().setConvivaUser();
  removeStorage('FOCUS_TARGET_ID');
  // togglePerformanceMonitor();

  // Initialize platform-specific functionality
  const platform = detectPlatform();
  logger.log('########## Detected platform:', platform);

  const supportedPlatforms = ['webos', 'tizen', 'vidaa', 'web'] as const;
  if (supportedPlatforms.includes(platform as any)) {
    initRemoteControl(platform as 'webos' | 'tizen' | 'vidaa' | 'web', focusManager);
  } else {
    // Handle unknown platform - default to web for basic functionality
    logger.warn('Unknown platform detected, defaulting to web mode:', platform);
    initRemoteControl('web', focusManager);
  }

  // Register Tizen media keys if on Tizen platform
  if (platform === 'tizen') {
    try {
      const registered = await registerTizenMediaKeys();
      if (registered) {
        logger.log('Tizen media keys registered successfully');
      } else {
        logger.warn('Failed to register Tizen media keys, FF/RW buttons may not work');
      }
    } catch (error) {
      logger.error('Error registering Tizen media keys:', error);
    }
  }

  // Initialize performance optimizations for TV (development only)
  // Start memory monitoring and frame rate monitoring in development
  if (process.env.NODE_ENV === 'development') {
    // Start frame rate monitoring with less aggressive warnings
    // Only warn if FPS is consistently low (not during initial load)
    let lowFpsCount = 0;
    frameRateMonitor.start(fps => {
      if (fps < 25 && fps > 0) {
        lowFpsCount++;
        // Only warn after 3 consecutive low FPS readings to avoid spam
        if (lowFpsCount >= 3) {
          logger.warn('Performance issue: FPS consistently low at', fps);
          lowFpsCount = 0; // Reset counter
        }
      } else {
        lowFpsCount = 0; // Reset if FPS recovers
      }
    });
  }

  // Preload critical modules in the background
  preloadCriticalModules();

  // Prewarm CDN origins (TCP/TLS handshake) before user navigates to player
  prewarmCdnOrigins();

  // Add keyboard shortcut to toggle performance monitor (Ctrl+Shift+P)
  document.addEventListener('keydown', handleKeyDown);
});

onUnmounted(() => {
  // Cleanup performance monitors (dev only)
  if (process.env.NODE_ENV === 'development') {
    memoryMonitor.stop();
    frameRateMonitor.stop();
  }

  // Remove event listener
  document.removeEventListener('keydown', handleKeyDown);
});

function handleKeyDown() {
  // Performance monitor removed - no keyboard shortcuts needed
}

const __returned__ = { showSplash, hiddenSplash, handleKeyDown, MenuBar, Splash, OnScreenDebugger }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})